import { CONTENT_PACKAGE_NONE_ELEMENT_ID } from '@features/campaigns/builder/consts/common';
import { CreateCampaignFormStep02Values } from '@features/campaigns/builder/containers/FormPages/FormPages.types';
import {
  createGeoTargetingPayloadDTO,
  getSelectedIdsFromTreeNodes,
  restoreGeoOperationType,
} from '@features/campaigns/builder/shared/utils/geoTargeting.dto.ts';
import {
  CampaignDetails,
  CampaignMediaType,
  CampaignMediaTypes,
  CampaignStatus,
  InvalidSectionsItem,
  OperationType,
} from '@features/campaigns/shared/types/campaign';
import { CampaignId } from '@shared/types/campaign';
import { ValueOf } from '@shared/types/common';
import { UserCountryCode } from '@shared/types/countryCodes.ts';

export interface UpdateCampaignStep02Payload {
  contentPackage: {
    id: string | null;
  } | null;
  provinces?: {
    operationType: ValueOf<typeof OperationType> | null;
    ids: string[];
  } | null;
  cities?: {
    operationType: ValueOf<typeof OperationType> | null;
    ids: string[];
  } | null;
  ageGender?: {
    operationType: ValueOf<typeof OperationType> | null;
    ids: string[];
  } | null;
  interestSegments?: {
    operationType: ValueOf<typeof OperationType> | null;
    ids: string[];
  } | null;
}

export interface RestoredStep02Data extends CreateCampaignFormStep02Values {
  id: CampaignId;
  mediaType: CampaignMediaType;
  status: CampaignStatus;
  invalidSections: InvalidSectionsItem[];
  country: UserCountryCode;
}

export function createCampaignStep02DTO(
  formValues: CreateCampaignFormStep02Values,
  mediaType: CampaignMediaType,
  country: UserCountryCode,
): UpdateCampaignStep02Payload {
  const {
    contentPackage,
    provinces,
    cities,
    provincesOperationType,
    ageGender,
    ageAndGenderOperationType,
    interestSegments,
    interestSegmentsOperationType,
  } = formValues;
  if (mediaType === CampaignMediaTypes.CROSS && contentPackage) {
    const payload: UpdateCampaignStep02Payload = {
      contentPackage: { id: contentPackage.id },
    };

    return payload;
  }

  const contentPackageValue = contentPackage?.id === CONTENT_PACKAGE_NONE_ELEMENT_ID ? null : contentPackage?.id;
  const geoTargetingPayload = createGeoTargetingPayloadDTO({ provinces, cities, provincesOperationType }, country);

  const ageAndGenderValue = getSelectedIdsFromTreeNodes(ageGender);
  const interestSegmentsValue = getSelectedIdsFromTreeNodes(interestSegments);

  const payload: UpdateCampaignStep02Payload = {
    contentPackage: null,
    provinces: null,
    cities: null,
    ageGender: null,
    interestSegments: null,
  };

  if (contentPackageValue) {
    payload.contentPackage = {
      id: contentPackageValue,
    };
  }

  payload.provinces = geoTargetingPayload.provinces;
  payload.cities = geoTargetingPayload.cities;

  if (ageAndGenderValue !== null) {
    payload.ageGender = {
      operationType: ageAndGenderOperationType,
      ids: ageAndGenderValue,
    };
  }

  if (interestSegmentsValue !== null) {
    payload.interestSegments = {
      operationType: interestSegmentsOperationType,
      ids: interestSegmentsValue,
    };
  }

  return payload;
}

export function getClearedTargetingFieldNames(
  baseline: RestoredStep02Data,
  payload: UpdateCampaignStep02Payload,
): string[] {
  const clearedFieldNames: string[] = [];

  if (baseline.contentPackage && !payload.contentPackage) {
    clearedFieldNames.push('contentPackage');
  }
  if (baseline.ageGender && !payload.ageGender) {
    clearedFieldNames.push('ageGender');
  }
  if (baseline.interestSegments && !payload.interestSegments) {
    clearedFieldNames.push('interestSegments');
  }

  return clearedFieldNames;
}

export function restoreStep02DataDTO(data: CampaignDetails | RestoredStep02Data): RestoredStep02Data {
  const operationType = restoreGeoOperationType(data);

  return {
    id: data.id,
    contentPackageOperationType: OperationType.INCLUDE,
    contentPackage: data.contentPackage,
    ageAndGenderOperationType: data.ageGender ? data.ageGender.operationType : null,
    ageGender: data.ageGender,
    interestSegments: data.interestSegments,
    interestSegmentsOperationType: data.interestSegments ? data.interestSegments.operationType : null,
    provinces: data.provinces,
    cities: data.cities,
    provincesOperationType: operationType,
    mediaType: data.mediaType,
    status: data.status,
    invalidSections: data.invalidSections,
    country: data.country,
  };
}
